// Single source of truth for the order lifecycle, shared by OrderPage (tabs,
// counts) and OrderList (status badge, contextual action button). The
// backend's Order schema already defines this exact set of statuses
// (back/models/Order.js) — nothing here invents a new state.
export const ORDER_STATUS = Object.freeze({
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETED: "completed",
  CANCELED: "canceled",
});

export const ORDER_STATUS_META = Object.freeze({
  [ORDER_STATUS.PENDING]: { label: "در انتظار", badgeClass: "bg-primary_1 text-primary" },
  [ORDER_STATUS.PROCESSING]: { label: "در حال آماده‌سازی", badgeClass: "bg-secondary_1 text-secondary" },
  [ORDER_STATUS.COMPLETED]: { label: "تکمیل شده", badgeClass: "bg-gray-100 text-gray-500" },
  [ORDER_STATUS.CANCELED]: { label: "لغو شده", badgeClass: "bg-red-50 text-red-500" },
});

// The single next lifecycle step available from a given status (drives the
// order card's one contextual action button), or null once an order is
// terminal (completed/canceled).
export const ORDER_NEXT_ACTION = Object.freeze({
  [ORDER_STATUS.PENDING]: { status: ORDER_STATUS.PROCESSING, label: "شروع آماده‌سازی" },
  [ORDER_STATUS.PROCESSING]: { status: ORDER_STATUS.COMPLETED, label: "تکمیل سفارش" },
  [ORDER_STATUS.COMPLETED]: null,
  [ORDER_STATUS.CANCELED]: null,
});

export const canCancelOrder = (status) =>
  status === ORDER_STATUS.PENDING || status === ORDER_STATUS.PROCESSING;

export const ORDER_STATUS_TABS = Object.freeze([
  { value: "all", label: "همه" },
  { value: ORDER_STATUS.PENDING, label: "در انتظار" },
  { value: ORDER_STATUS.PROCESSING, label: "در حال آماده‌سازی" },
  { value: ORDER_STATUS.COMPLETED, label: "تکمیل شده" },
  { value: ORDER_STATUS.CANCELED, label: "لغو شده" },
]);

export const getOrderStatusMeta = (status) =>
  ORDER_STATUS_META[status] || { label: status || "نامشخص", badgeClass: "bg-gray-100 text-gray-500" };

const normalize = (value) => (value || "").toString().trim();

// Cheap client-side search over table number, invoice number, and item
// names — the dashboard's order volume doesn't warrant a backend search
// endpoint yet, so this stays a plain in-memory filter.
export const searchOrders = (orders, query) => {
  const term = normalize(query).toLowerCase();
  if (!term) return orders;
  return orders.filter((order) => {
    const table = normalize(order.tableNumber).toLowerCase();
    const invoice = normalize(order.invoiceNumber).toLowerCase();
    const itemNames = (order.items || []).map((item) => normalize(item.name).toLowerCase()).join(" ");
    return table.includes(term) || invoice.includes(term) || itemNames.includes(term);
  });
};

export const filterOrdersByStatus = (orders, status) =>
  status === "all" ? orders : orders.filter((order) => order.status === status);

export const countOrdersByStatus = (orders) =>
  orders.reduce((counts, order) => {
    counts[order.status] = (counts[order.status] || 0) + 1;
    return counts;
  }, {});
